/**
 * Platform Metrics Page
 * Shows platform-wide summary metrics and tenant distribution.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  MapPin,
  Calendar,
  AlertTriangle,
  TrendingUp,
  ArrowUpRight,
} from 'lucide-react';

export default function PlatformMetricsPage() {
  const { api } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get('/platform/metrics');
        setMetrics(data);
      } catch (err) {
        toast.error('Failed to load metrics');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [api]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div data-testid="platform-metrics-page" className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Platform Metrics</h1>
        <p className="text-sm text-zinc-500 mt-0.5">Overview of the entire Rapid X platform</p>
      </div>

      {/* Primary Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard icon={Building2} label="Tenants" value={metrics.total_tenants} accent="amber" />
        <MetricCard icon={Building2} label="Active" value={metrics.active_tenants} accent="emerald" />
        <MetricCard icon={Building2} label="Suspended" value={metrics.suspended_tenants} accent="red" />
        <MetricCard icon={Users} label="Total Users" value={metrics.total_users} accent="blue" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <MetricCard icon={MapPin} label="Total Sites" value={metrics.total_sites} accent="violet" />
        <MetricCard icon={Calendar} label="Total Shifts" value={metrics.total_shifts} accent="cyan" />
        <MetricCard icon={AlertTriangle} label="Total Incidents" value={metrics.total_incidents} accent="orange" />
      </div>

      {/* Plan Distribution */}
      <div className="bg-[#111114] border border-zinc-800/60 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider mb-4">Tenants by Plan</h3>
        {Object.keys(metrics.tenants_by_plan || {}).length === 0 ? (
          <p className="text-sm text-zinc-500">No plan data available</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(metrics.tenants_by_plan).map(([plan, count]) => {
              const total = metrics.total_tenants || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={plan}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-zinc-300 capitalize">{plan}</span>
                    <span className="text-xs text-zinc-500">{count} ({pct}%)</span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-500/70 rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Provisions */}
      <div className="bg-[#111114] border border-zinc-800/60 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider mb-4">
          Recent Provisioning Events
        </h3>
        {(metrics.recent_provisions || []).length === 0 ? (
          <p className="text-sm text-zinc-500">No recent provisions</p>
        ) : (
          <div className="space-y-2">
            {metrics.recent_provisions.map((evt, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 rounded-md bg-zinc-900/40 border border-zinc-800/30">
                <div>
                  <p className="text-sm text-white">{evt.tenant_name}</p>
                  <p className="text-xs text-zinc-500 font-mono">
  {evt.tenant_domain || `${evt.tenant_slug}.rapidxtech.com.au`}
</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-zinc-500">{evt.performed_by}</p>
                  <p className="text-[10px] text-zinc-600 font-mono">
                    {evt.timestamp ? new Date(evt.timestamp).toLocaleDateString('en-AU') : '—'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, accent }) {
  const colors = {
    amber: 'border-amber-900/30 bg-amber-500/5',
    emerald: 'border-emerald-900/30 bg-emerald-500/5',
    red: 'border-red-900/30 bg-red-500/5',
    blue: 'border-blue-900/30 bg-blue-500/5',
    violet: 'border-violet-900/30 bg-violet-500/5',
    cyan: 'border-cyan-900/30 bg-cyan-500/5',
    orange: 'border-orange-900/30 bg-orange-500/5',
  };

  const iconColors = {
    amber: 'text-amber-500',
    emerald: 'text-emerald-500',
    red: 'text-red-500',
    blue: 'text-blue-500',
    violet: 'text-violet-500',
    cyan: 'text-cyan-500',
    orange: 'text-orange-500',
  };

  return (
    <div className={`rounded-lg border p-4 ${colors[accent] || colors.amber}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${iconColors[accent] || iconColors.amber}`} />
        <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value ?? '—'}</p>
    </div>
  );
}
